// SPDX-License-Identifier: AGPL-3.0-or-later

import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/profile/profile_card/ProfileCardActions.module.css';
import UserNote from '@app/features/user/state/UserNote';
import {useLingui} from '@lingui/react/macro';
import {ListPlusIcon, SnowflakeIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef} from 'react';

interface ProfileCardActionsProps {
	userId: string;
	isHovering: boolean;
	onNoteClick: () => void;
}
export const ProfileCardActions: React.FC<ProfileCardActionsProps> = observer(({userId, isHovering, onNoteClick}) => {
	const {t, i18n} = useLingui();
	const userNote = UserNote.getUserNote(userId);
	const noteButtonRef = useRef<HTMLButtonElement>(null);
	const copyIdButtonRef = useRef<HTMLButtonElement>(null);
	return (
		<>
			<div className={clsx(styles.noteButtonContainer, isHovering && styles.noteButtonContainerVisible)}>
				<FocusRing offset={-2} focusTarget={noteButtonRef} ringTarget={noteButtonRef}>
					<Tooltip
						text={userNote ? () => <div className={styles.noteTooltipContent}>{userNote}</div> : t`Add Note`}
						maxWidth="none"
					>
						<button
							ref={noteButtonRef}
							type="button"
							onClick={onNoteClick}
							className={styles.noteButton}
							aria-label={userNote ? t`Edit Note` : t`Add Note`}
						>
							<ListPlusIcon className={clsx(styles.iconMedium, styles.noteIconWrapper)} />
						</button>
					</Tooltip>
				</FocusRing>
			</div>
			<div className={clsx(styles.copyIdButtonContainer, isHovering && styles.copyIdButtonContainerVisible)}>
				<FocusRing offset={-2} focusTarget={copyIdButtonRef} ringTarget={copyIdButtonRef}>
					<Tooltip text={t`Copy User ID`} maxWidth="none">
						<button
							ref={copyIdButtonRef}
							type="button"
							onClick={() => TextCopyCommands.copy(i18n, userId)}
							className={styles.copyIdButton}
							aria-label={t`Copy User ID`}
						>
							<SnowflakeIcon weight="bold" className={clsx(styles.iconMedium, styles.copyIdIconWrapper)} />
						</button>
					</Tooltip>
				</FocusRing>
			</div>
		</>
	);
});
